import { Toggle } from '@ori-ui/components';
import { ArrowLeftThin } from '@ori-ui/icons';
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  styled,
  InputLabel,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from '@ori-ui/mui';
import type {
  GlobalCustomerRegistrationsRegistrationSponsorDetailsDto,
  GlobalCustomerRegistrationsRegistrationSponsorInfoDto,
} from '@ori/global-api-client-react';
import type {
  StaticCustomerRegistrationsMetadataRegistrationSponsorDetailsMetadataDto,
  StaticCustomerRegistrationsMetadataRegistrationSponsorDetailsMetadataDtoPreselectedSponsorSelectionMethod,
} from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import type { ChangeEvent, FC } from 'react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Localization } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useStepperData, useFormError, useVirtualPageAnalyticsData } from '../../../contexts';
import { TranslationKey } from '../../../models';
import { useAddressFormatMetadata } from '../../../models/api';
import { getOptionLabelSingleLine, defaultAddressFormat } from '../../../utils';
import { AddressAutocomplete } from '../../AddressAutocomplete';
import { TextFormFieldByConfig } from '../../FieldsByConfig';
import { FormValidationSummary } from '../../FormValidationSummary';
import { SponsorBox } from './SponsorBox';

enum selectBpVariant {
  findSponsor = 'SponsorLocator',
  knowSponsorNumber = 'ManualInput',
}

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  width: '100%',
  marginTop: theme.spacing(8),
}));

const SubmitButtton = styled(Button)(({ theme }) => ({
  minWidth: '330px',
  marginTop: theme.spacing(3),
}));

const FindButtton = styled(Button)(({ theme }) => ({
  width: '330px',
  maxWidth: '100%',
  height: '35px',
  margin: '0 auto',
  marginBottom: theme.spacing(10),
}));

const BackButtton = styled(Button)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  minWidth: '60px',
  marginTop: theme.spacing(2),
}));

const StyledArrowIcon = styled(ArrowLeftThin)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const AddressAutocompleteWrapper = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(6),
}));

const StyledToggle = styled(Toggle)(({ theme }) => ({
  cursor: 'pointer',
}));

const StyledSingleLineLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(5),
}));

interface IConnectToBPFormProps {
  initialData?: GlobalCustomerRegistrationsRegistrationSponsorDetailsDto;
  config?: StaticCustomerRegistrationsMetadataRegistrationSponsorDetailsMetadataDto;
  sponsorInfo?: GlobalCustomerRegistrationsRegistrationSponsorInfoDto;
  profileSponsorImage?: string;
  isDefaultAddress: boolean;
  isSuccessSubmit: boolean;
  onSubmitForm: (data: GlobalCustomerRegistrationsRegistrationSponsorDetailsDto) => void;
}

export const ConnectToBPForm: FC<IConnectToBPFormProps> = (props) => {
  const { initialData, config, sponsorInfo, profileSponsorImage, isDefaultAddress, isSuccessSubmit, onSubmitForm } =
    props;
  const {
    context: { stepperData },
  } = useStepperData();
  const { activeStep, setActiveStep, stepsOrder } = stepperData;

  const defaultSelectSponsorType = config?.preselectedSponsorSelectionMethod ?? 'ManualInput';

  const {
    context: { errorData },
  } = useFormError();
  const { setErrorData, customErrors } = errorData;

  const [selectSponsorType, setSelectSponsorType] = useState(defaultSelectSponsorType);
  const [country, setCountry] = useState<boolean | string>(false);
  const [resetKey, setResetKey] = useState(0);

  const { getTestIdProps } = useTestId();

  const methods = useForm<GlobalCustomerRegistrationsRegistrationSponsorDetailsDto>({
    defaultValues: initialData,
    reValidateMode: 'onChange',
    mode: 'all',
  });

  const { formState, watch, setValue } = methods;

  const { isValid, isSubmitting } = formState;
  const countryOptions = config?.address?.country?.availableOptions ?? [];

  const values = watch();

  const invalidCodeErrors = customErrors?.filter(
    (er) =>
      !Object.keys(values)
        .map((val) => val.toLowerCase())
        .includes(er.fieldName?.toLowerCase() ?? ''),
  );

  const countryFieldName = 'address.country';

  const { data: formatAddressFields } = useAddressFormatMetadata('SingleLine');

  const {
    virtualPageAnalyticsContext: { virtualPageAnalyticsData },
  } = useVirtualPageAnalyticsData();
  const { setPreviousStep } = virtualPageAnalyticsData;

  const handleChangeSponsorFindType = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectSponsorType(
      event.target
        .value as StaticCustomerRegistrationsMetadataRegistrationSponsorDetailsMetadataDtoPreselectedSponsorSelectionMethod,
    );
    setResetKey(Date.now());
    methods.reset(initialData);
    methods.clearErrors();
    setCountry(false);
    if (customErrors && customErrors.length > 0) {
      setErrorData([]);
    }
    setErrorData([]);
  };

  const handleBack = () => {
    setPreviousStep(stepsOrder[activeStep]);
    setActiveStep(activeStep - 1);
  };

  const handleNext = () => {
    setPreviousStep(stepsOrder[activeStep]);
    setActiveStep(activeStep + 1);
  };

  useEffect(() => {
    if (values.address?.zipcode) {
      setSelectSponsorType(selectBpVariant.findSponsor);
    }
  }, [values.address?.zipcode]);

  useEffect(() => {
    if (config?.address?.country?.isAvailable && selectSponsorType === selectBpVariant.findSponsor) {
      setValue(countryFieldName, countryOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps --- not needed other property
  }, [selectSponsorType]);

  const isKnowSponsorNumber = selectSponsorType === selectBpVariant.knowSponsorNumber;
  const isFindSponsorSelected = selectSponsorType === selectBpVariant.findSponsor;

  const getAddressAutocomplete = () => {
    if (config?.address?.searchOptions?.isEnabled) {
      return (
        <AddressAutocomplete
          {...config.address}
          isEditable={isFindSponsorSelected ? !sponsorInfo?.consultantNumber : true}
          tooltipText={
            <Localization
              id={TranslationKey.SecondStep_FindTheBestBrandPartner_Tooltip}
              defaultMessage={defaultTranslations.SecondStep_FindTheBestBrandPartner_Tooltip}
            />
          }
        />
      );
    }

    return null;
  };

  const handleChangeCountry = (countryProperty: boolean | string) => {
    setCountry(countryProperty);
    if (typeof countryProperty === 'boolean') {
      if (countryProperty) {
        setValue(countryFieldName, countryOptions[1]);
      } else {
        setValue(countryFieldName, countryOptions[0]);
      }
    } else {
      setValue(countryFieldName, countryProperty);
    }
  };

  const getCountryField = () => {
    if (countryOptions.length === 2) {
      return (
        <Box mb={config?.address?.searchOptions?.isEnabled ? 5 : 0}>
          <InputLabel
            id="country-select-sponsorForm"
            required={isFindSponsorSelected}
          >
            <Localization
              id={TranslationKey.SecondStep_Country}
              defaultMessage={defaultTranslations.SecondStep_Country}
            />
          </InputLabel>
          <StyledToggle
            fullWidth
            checked={Boolean(country)}
            firstText={countryOptions[0]}
            secondText={countryOptions[1]}
            onChange={({ currentTarget: { checked } }) => handleChangeCountry(checked)}
          />
        </Box>
      );
    }
    return (
      <FormControl
        fullWidth
        required={isFindSponsorSelected}
        sx={{ marginBottom: config?.address?.searchOptions?.isEnabled ? 5 : 0 }}
      >
        <InputLabel id="country-select-sponsorForm">
          <Localization
            id={TranslationKey.SecondStep_Country}
            defaultMessage={defaultTranslations.SecondStep_Country}
          />
        </InputLabel>
        <Select
          value={country ? String(country) : undefined}
          fullWidth
          labelId="country-select-sponsorForm"
          label={
            <Localization
              id={TranslationKey.SecondStep_Country}
              defaultMessage={defaultTranslations.SecondStep_Country}
            />
          }
          onChange={(e) => handleChangeCountry(e.target.value)}
        >
          {countryOptions.sort().map((opt) => (
            <MenuItem
              key={opt}
              value={opt}
            >
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const getCorrectSubmitButton = () => {
    if (isFindSponsorSelected) {
      return (
        <SubmitButtton
          variant="contained"
          color="primary"
          type="button"
          disabled={!sponsorInfo?.consultantNumber}
          onClick={() => handleNext()}
          {...getTestIdProps('submit-btn-sponsorForm')}
        >
          <Localization
            id={TranslationKey.SecondStep_Button}
            defaultMessage={defaultTranslations.SecondStep_Button}
          />
        </SubmitButtton>
      );
    }

    return (
      <SubmitButtton
        variant="contained"
        color="primary"
        type="submit"
        disabled={!isValid || isSubmitting || isSuccessSubmit}
        {...getTestIdProps('submit-btn-sponsorForm')}
      >
        <Localization
          id={TranslationKey.SecondStep_Button}
          defaultMessage={defaultTranslations.SecondStep_Button}
        />
      </SubmitButtton>
    );
  };

  const addressAutocomplete = getAddressAutocomplete();

  const countryField = getCountryField();

  const isDisabledFind = !(values.address?.country ?? values.address?.zipcode);
  const submitButton = getCorrectSubmitButton();

  const knowSponsorLabelVariant = sponsorInfo?.consultantNumber ? 'body2' : 'body1';

  const formatedAddress = values.address?.zipcode
    ? getOptionLabelSingleLine(0, values.address, formatAddressFields ?? defaultAddressFormat)
    : null;

  const singleLineAddress =
    config?.address?.searchOptions?.isEnabled && formatedAddress ? (
      <>
        <StyledSingleLineLabel variant="caption">
          <Localization
            id={TranslationKey.SecondStep_SearchedAddress}
            defaultMessage={defaultTranslations.SecondStep_SearchedAddress}
          />
        </StyledSingleLineLabel>
        <Typography
          variant="body1"
          sx={{ wordWrap: 'break-word' }}
        >
          {formatedAddress}
        </Typography>
      </>
    ) : null;

  return (
    <FormProvider {...methods}>
      <FormValidationSummary invalidPropertyErrors={invalidCodeErrors} />
      <StyledForm
        noValidate
        onSubmit={methods.handleSubmit(onSubmitForm)}
      >
        {sponsorInfo?.consultantNumber ? (
          <SponsorBox
            isVisibleNextButton
            sponsorDetailInfo={sponsorInfo}
            sponsorImage={profileSponsorImage}
            title={
              <Localization
                id={TranslationKey.SecondStep_HeresYourBrandPartner}
                defaultMessage={defaultTranslations.SecondStep_HeresYourBrandPartner}
              />
            }
          />
        ) : (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <RadioGroup
              name="brand-partner"
              value={selectSponsorType}
              sx={{ width: '100%' }}
              onChange={handleChangeSponsorFindType}
            >
              <Box
                width="100%"
                display="flex"
                flexDirection="column"
              >
                <Box
                  component="span"
                  width="100%"
                >
                  <StyledFormControlLabel
                    value={selectBpVariant.knowSponsorNumber}
                    control={<Radio color="primary" />}
                    disabled={Boolean(sponsorInfo?.consultantNumber)}
                    label={
                      <Typography
                        variant={knowSponsorLabelVariant}
                        sx={{
                          fontWeight: selectSponsorType === selectBpVariant.knowSponsorNumber ? 'bold' : 'normal',
                        }}
                      >
                        <Localization
                          id={TranslationKey.SecondStep_IKnowaBrandPartner}
                          defaultMessage={defaultTranslations.SecondStep_IKnowaBrandPartner}
                        />
                      </Typography>
                    }
                  />
                  {isKnowSponsorNumber ? (
                    <TextFormFieldByConfig
                      key={resetKey}
                      inputType="text"
                      keyboardType="numeric"
                      name="sponsorNumber"
                      label={
                        <Localization
                          id={TranslationKey.SecondStep_BrandPartnerNumber}
                          defaultMessage={defaultTranslations.SecondStep_BrandPartnerNumber}
                        />
                      }
                      tooltipText={
                        <Localization
                          id={TranslationKey.SecondStep_IKnowaBrandPartner}
                          defaultMessage={defaultTranslations.SecondStep_IKnowaBrandPartner}
                        />
                      }
                      {...config?.sponsorNumber}
                      allowOnlyNumbers
                      isEditable={isKnowSponsorNumber}
                      validation={{ isMandatory: isKnowSponsorNumber }}
                    />
                  ) : null}
                </Box>
                <Box
                  component="span"
                  width="100%"
                  display="flex"
                  flexDirection="column"
                >
                  <StyledFormControlLabel
                    value={selectBpVariant.findSponsor}
                    control={<Radio color="primary" />}
                    label={
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: selectSponsorType === selectBpVariant.findSponsor ? 'bold' : 'normal',
                        }}
                      >
                        <Localization
                          id={TranslationKey.SecondStep_FindTheBestBrandPartner}
                          defaultMessage={defaultTranslations.SecondStep_FindTheBestBrandPartner}
                        />
                      </Typography>
                    }
                    style={{ order: 0 }}
                  />
                  {isFindSponsorSelected && !sponsorInfo?.consultantNumber ? (
                    <>
                      {config?.address?.country?.isAvailable && !isDefaultAddress ? (
                        <div {...getTestIdProps('country-select-sponsorForm')}>{countryField}</div>
                      ) : null}
                      {initialData?.address?.zipcode ? null : (
                        <>
                          <AddressAutocompleteWrapper style={{ order: config?.address?.searchOptions?.displayOrder }}>
                            {addressAutocomplete}
                            {singleLineAddress}
                          </AddressAutocompleteWrapper>

                          <TextFormFieldByConfig
                            name="address.zipcode"
                            label={
                              <Localization
                                id={TranslationKey.SecondStep_YourZipCode}
                                defaultMessage={defaultTranslations.SecondStep_YourZipCode}
                              />
                            }
                            tooltipText={
                              <Localization
                                id={TranslationKey.SecondStep_YourZipCode_Tooltip}
                                defaultMessage={defaultTranslations.SecondStep_YourZipCode_Tooltip}
                              />
                            }
                            {...config?.address?.zipcode}
                            isAddressMatchRequired
                            validation={{ isMandatory: isFindSponsorSelected }}
                          />
                        </>
                      )}

                      <FindButtton
                        // this number is count of all possible inputs for address + 1 because we want as last
                        style={{ order: 16 }}
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isDisabledFind || isSubmitting || isSuccessSubmit}
                      >
                        <Localization
                          id={TranslationKey.SecondStep_Button_Find}
                          defaultMessage={defaultTranslations.SecondStep_Button_Find}
                        />
                      </FindButtton>
                    </>
                  ) : null}
                  {sponsorInfo?.consultantNumber ? (
                    <div style={{ order: 16 }}>
                      <SponsorBox
                        sponsorDetailInfo={sponsorInfo}
                        sponsorImage={profileSponsorImage}
                        title={
                          <Localization
                            id={TranslationKey.SecondStep_HeresYourBrandPartner}
                            defaultMessage={defaultTranslations.SecondStep_HeresYourBrandPartner}
                          />
                        }
                        isVisibleNextButton={false}
                      />
                    </div>
                  ) : null}
                </Box>
              </Box>
            </RadioGroup>
            {submitButton}
          </Grid>
        )}
        <BackButtton
          variant="text"
          color="inherit"
          onClick={() => handleBack()}
        >
          <StyledArrowIcon />
          <Localization
            id={TranslationKey.back}
            defaultMessage={defaultTranslations.back}
          />
        </BackButtton>
      </StyledForm>
    </FormProvider>
  );
};
